import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, Minus, Flame, BarChart3, Loader2, ArrowRight, Sparkles, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrendsPage() {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrends();
  }, []);

  const fetchTrends = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/genome/trends`);
      setTrends(response.data);
    } catch (error) {
      console.error('Failed to load trends:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (prediction) => {
    if (prediction === 'Rising Star') return <Flame className="w-4 h-4 text-red-500" />;
    if (prediction === 'Growing') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (prediction === 'Stable') return <Minus className="w-4 h-4 text-gray-400" />;
    return <TrendingDown className="w-4 h-4 text-red-400" />;
  };

  const getTrendColor = (prediction) => {
    if (prediction === 'Rising Star') return 'bg-red-100 text-red-700';
    if (prediction === 'Growing') return 'bg-green-100 text-green-700';
    if (prediction === 'Stable') return 'bg-gray-100 text-gray-700';
    return 'bg-orange-100 text-orange-700';
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SEOHead
        title={trends?.seo_title || 'B2B Event Industry Trends'}
        description={trends?.seo_description}
      />

      {/* Hero */}
      <section className="bg-gradient-to-r from-purple-900 via-indigo-900 to-purple-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">AI-Powered Analysis</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Event Industry Trends {new Date().getFullYear()}
          </h1>
          <p className="text-xl text-purple-200 max-w-2xl mx-auto">
            Discover what's hot in B2B events. AI analysis of {trends?.analysis_based_on}.
          </p>
        </div>
      </section>

      {/* Trending Topics */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <TrendingUp className="w-6 h-6 text-green-500" />
            <h2 className="text-2xl font-bold text-gray-900">Trending Topics</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {trends?.trending_topics?.map((topic, index) => (
              <div
                key={topic.topic}
                className="p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-purple-200 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-gray-300">#{index + 1}</span>
                    {getTrendIcon(topic.prediction)}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTrendColor(topic.prediction)}`}>
                    {topic.prediction}
                  </span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{topic.topic}</h3>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{topic.event_count} events</span>
                  <span className={topic.growth_yoy?.startsWith('+') ? 'text-green-600 font-medium' : 'text-gray-500'}>
                    {topic.growth_yoy} YoY
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Predictions */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <BarChart3 className="w-6 h-6 text-purple-500" />
            <h2 className="text-2xl font-bold text-gray-900">6-Month Predictions</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {trends?.predictions?.next_6_months?.map((pred, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{pred.trend}</h3>
                    <p className="text-gray-600 mb-2">{pred.prediction}</p>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      pred.confidence === 'high' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {pred.confidence} confidence
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Emerging Themes */}
          {trends?.predictions?.emerging_themes && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🚀 Emerging Themes</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {trends.predictions.emerging_themes.map((theme, i) => (
                  <div key={i} className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
                    <h4 className="font-medium text-gray-900">{theme.theme}</h4>
                    <div className="flex items-center justify-between mt-2 text-sm">
                      <span className="text-green-600 font-bold">{theme.growth}</span>
                      <span className="text-gray-500">{theme.key_events} events</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-purple-200" />
          <h2 className="text-2xl font-bold mb-4">Stay Ahead of the Curve</h2>
          <p className="text-purple-100 mb-6">Browse upcoming events in trending categories and plan your 2026 calendar.</p>
          <Link to="/events">
            <Button className="bg-white text-purple-600 hover:bg-gray-100">
              Explore Events <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </MainLayout>
  );
}